import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import TextFieldsOutlinedIcon from '@mui/icons-material/TextFieldsOutlined';
import { useFormContext } from 'react-hook-form';
import { FieldTypeEnum, ICustomField } from 'wisestep-api';
import {
  ControlledTextInput,
  WsAutoComplete,
  WsDatePicker,
  WsLocationSearch,
  WsSelect,
  WsTypoColors,
} from 'wisestep-base';

import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import WsAccordion from '../../../components/filters/components/WsAccordion';
import WsCheckBox from '../../../components/WsCheckBox/WsCheckBox';

interface CustomFieldFilterProps {
  field: ICustomField;
  token: string;
  authorization: string;
}

const FieldIconMap = {
  TextBox: TextFieldsOutlinedIcon,
  Number: PinOutlinedIcon,
  Date: CalendarMonthOutlinedIcon,
  DateAndTime: AccessTimeOutlinedIcon,
  Location: LocationOnOutlinedIcon,
  URL: LinkOutlinedIcon,
  Dropdown: LocalOfferOutlinedIcon,
  Users: GroupOutlinedIcon,
  Checkbox: CheckBoxOutlinedIcon,
};

const CustomFieldFilter = ({ field, token, authorization }: CustomFieldFilterProps) => {
  const { getValues, watch, setValue } = useFormContext();
  const fieldName = `customFieldFilters.${field.name}`;
  const FieldIcon = FieldIconMap[field.type as keyof typeof FieldIconMap] || TextFieldsOutlinedIcon;

  const renderFilterInput = () => {
    switch (field.type) {
      case FieldTypeEnum.TextBox:
      case FieldTypeEnum.Number:
      case FieldTypeEnum.URL:
        return (
          <ControlledTextInput
            name={fieldName}
            labelText={field.label}
            size="small"
            variant="outlined"
            fullWidth
          />
        );
      case FieldTypeEnum.Date:
        return <WsDatePicker name={fieldName} label="" size="small" required={false} />;

      case FieldTypeEnum.Location:
        return (
          <WsLocationSearch
            name={fieldName}
            multiSelect={false}
            wisestepApi={true}
            token={token}
            authorization={authorization}
            size="small"
            placeHolder={`Search ${field.label}`}
          />
        );

      case FieldTypeEnum.Dropdown:
        if (field.multiple) {
          return (
            <WsAutoComplete
              name={fieldName}
              initialValue={watch(fieldName) || []}
              onTagsChange={(value) => setValue(fieldName, value)}
              placeholder={`Select ${field.label}`}
              size="small"
              multiple={true}
              optionsInStringArray={field.options || []}
            />
          );
        } else {
          return (
            <WsSelect
              name={fieldName}
              label=""
              selectArray={(field.options || []).map((option) => ({
                label: option,
                value: option,
              }))}
              size="small"
            />
          );
        }

      case FieldTypeEnum.Checkbox:
        if (field.options && field.options.length > 0) {
          return (
            <WsAutoComplete
              name={fieldName}
              initialValue={watch(fieldName) || []}
              onTagsChange={(value) => setValue(fieldName, value)}
              placeholder={`Select ${field.label}`}
              size="small"
              multiple={true}
              optionsInStringArray={field.options || []}
            />
          );
        } else {
          return <WsCheckBox name={fieldName} label="" />;
        }

      case FieldTypeEnum.Users:
        return (
          <RecruiterSuggestions
            name={fieldName}
            multiple={field.multiple || false}
            defaultValue={getValues(fieldName)}
            value={watch(fieldName)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <WsAccordion
      title={field.label || field.name || ''}
      titleColor={WsTypoColors.GREYTEXT}
      startIcon={<FieldIcon />}
    >
      {renderFilterInput()}
    </WsAccordion>
  );
};

export default CustomFieldFilter;
